import React, { useEffect, useState } from "react";
import { CalendarDays, Clock3 } from "lucide-react";
import { it } from "date-fns/locale";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const toDate = (s) => (s ? new Date(`${s}T00:00:00`) : undefined);
const fIt = (s) => (s ? s.split("-").reverse().join("/") : "");
const maschera = (t) => { const d = t.replace(/\D/g, "").slice(0, 8); return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join("/"); };
const daTesto = (t) => {
  const m = t.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(+m[3], +m[2] - 1, +m[1]);
  return d.getDate() === +m[1] && d.getMonth() === +m[2] - 1 ? toIso(d) : null;
};

export function DataIt({ value, onChange, min, max, disabled, className = "", testid, placeholder = "gg/mm/aaaa" }) {
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState(fIt(value));
  const [bad, setBad] = useState(false);
  useEffect(() => { setTxt(fIt(value)); setBad(false); }, [value]);
  const scrivi = (t) => {
    const m = maschera(t); setTxt(m);
    const iso = daTesto(m);
    if (iso && (!min || iso >= min) && (!max || iso <= max)) { setBad(false); onChange(iso); } else setBad(m.length === 10);
  };
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <div className={`inline-flex items-center rounded-md border bg-white h-8 ${bad ? "border-red-400 ring-1 ring-red-300" : ""} ${disabled ? "opacity-50" : ""} ${className}`}>
        <input value={txt} onChange={(e) => scrivi(e.target.value)} onBlur={() => { if (daTesto(txt) !== value) { setTxt(fIt(value)); setBad(false); } }} inputMode="numeric" placeholder={placeholder} disabled={disabled}
          className="w-full min-w-0 bg-transparent px-2 text-sm font-semibold tabular-nums outline-none placeholder:font-normal placeholder:text-slate-400" data-testid={testid} />
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} className="h-full px-2 border-l text-teal-600 hover:bg-secondary rounded-r-md" title="Apri il calendario" data-testid={testid ? `${testid}-apri` : undefined}><CalendarDays size={15} /></button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0 z-[200]" align="start" data-testid={testid ? `${testid}-calendario` : undefined}>
        <Calendar mode="single" locale={it} weekStartsOn={1} selected={toDate(value)} defaultMonth={toDate(value) || toDate(min)}
          disabled={min || max ? [...(min ? [{ before: toDate(min) }] : []), ...(max ? [{ after: toDate(max) }] : [])] : undefined} onSelect={(d) => { if (d) { onChange(toIso(d)); setOpen(false); } }} initialFocus />
      </PopoverContent>
    </Popover>
  );
}


const ORE = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MIN = ["00", "15", "30", "45"];
const mascheraOra = (t) => { const d = t.replace(/\D/g, "").slice(0, 4); return d.length > 2 ? `${d.slice(0, 2)}:${d.slice(2)}` : d; };

export function OraIt({ value, onChange, className = "", testid }) {
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState(value || "");
  useEffect(() => { setTxt(value || ""); }, [value]);
  const [h, m] = (value || "").split(":");
  const scrivi = (t) => { const x = mascheraOra(t); setTxt(x); if (!x) onChange(""); else if (/^([01]\d|2[0-3]):[0-5]\d$/.test(x)) onChange(x); };
  const cella = (on) => `h-7 rounded text-xs font-semibold tabular-nums ${on ? "bg-teal-600 text-white" : "hover:bg-secondary"}`;
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <div className={`inline-flex items-center rounded-md border bg-white h-9 ${className}`}>
        <input value={txt} onChange={(e) => scrivi(e.target.value)} onBlur={() => { if (txt && !/^([01]\d|2[0-3]):[0-5]\d$/.test(txt)) setTxt(value || ""); }} inputMode="numeric" placeholder="hh:mm"
          className="w-full min-w-0 bg-transparent px-2 text-sm font-semibold tabular-nums outline-none placeholder:font-normal placeholder:text-slate-400" data-testid={testid} />
        <PopoverTrigger asChild><button type="button" className="h-full px-2 border-l text-teal-600 hover:bg-secondary rounded-r-md" title="Scegli l'ora" data-testid={testid ? `${testid}-apri` : undefined}><Clock3 size={15} /></button></PopoverTrigger>
      </div>
      <PopoverContent className="w-64 p-2 z-[200]" align="start" data-testid={testid ? `${testid}-orologio` : undefined}>
        <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Ora</div>
        <div className="grid grid-cols-6 gap-1">{ORE.map((o) => <button key={o} type="button" className={cella(o === h)} onClick={() => onChange(`${o}:${m || "00"}`)} data-testid={testid ? `${testid}-h-${o}` : undefined}>{o}</button>)}</div>
        <div className="text-[10px] font-bold uppercase text-slate-500 mt-2 mb-1">Minuti</div>
        <div className="grid grid-cols-4 gap-1">{MIN.map((x) => <button key={x} type="button" className={cella(x === m)} onClick={() => { onChange(`${h || "08"}:${x}`); setOpen(false); }} data-testid={testid ? `${testid}-m-${x}` : undefined}>:{x}</button>)}</div>
        {value && <button type="button" className="mt-2 text-[11px] text-slate-500 underline" onClick={() => { onChange(""); setOpen(false); }}>Togli l'ora</button>}
      </PopoverContent>
    </Popover>
  );
}
